/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2022 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */

import {jwtDecode, JwtPayload} from "jwt-decode";
import {ApiError} from "../cart/requestHandlers/ApiError";
import {IAddressResponseData} from "../checkout/AddressResponseData";
import {EventEmitter} from "../events/EventEmitter";
import {IEventEmitter} from "../events/IEventEmitter";
import {
  CustomerRequestHandler,
  ICredentials,
  ICustomerAddress,
  ICustomerPutParams,
  ICustomerRatingsResult,
  INewsletter,
  IPoints,
  ReceiptType,
} from "./requestHandlers/CustomerRequestHandler";
import {IReceiptData} from "./receipt/ReceiptData";
import {IVisitorData} from "./VisitorData";

export interface ICustomerDataset {
  visitorData: IVisitorData;
  token: IOurJwtToken | null;
}

export class Customer {
  /**
   * Creates and verifies a new Customer from a visitor
   * @param {IVisitorData} visitorData
   * @returns {Promise<Customer>}
   */
  public static create(visitorData: IVisitorData): Customer {
    return new Customer(visitorData);
  }

  /**
   * Verifies the token structure and its signature against the public key
   * @param {string} token
   * @returns {IOurJwtToken}
   */
  private static verifyToken(token: string) {
    try {
      return jwtDecode<IOurJwtToken>(token);
    } catch (e) {
      throw new ApiError("JWT decode failed: ", e);
    }
  }

  private visitorData: IVisitorData;
  private parsedToken: IOurJwtToken | null = null;
  private readonly eventEmitter: IEventEmitter<ICustomerDataset>;

  private constructor(visitorData: IVisitorData) {
    this.visitorData = visitorData;
    if (visitorData.token) {
      this.parsedToken = Customer.verifyToken(visitorData.token);
    }

    this.eventEmitter = new EventEmitter<ICustomerDataset>();
    this.eventEmitter.on((payload: ICustomerDataset) => {
      if (payload.visitorData === this.visitorData) {
        return;
      }
      this.visitorData = payload.visitorData;
      this.parsedToken = payload.token;
    });

    this.logIn = this.logIn.bind(this);
    this.logOut = this.logOut.bind(this);
    this.hasRole = this.hasRole.bind(this);
    this.getAvailablePoints = this.getAvailablePoints.bind(this);
    this.getLastAddresses = this.getLastAddresses.bind(this);
    this.setNewsletterOptions = this.setNewsletterOptions.bind(this);
    this.getNewsletterOptions = this.getNewsletterOptions.bind(this);
    this.getRatings = this.getRatings.bind(this);
    this.retrieveProperties = this.retrieveProperties.bind(this);
    this.putProperties = this.putProperties.bind(this);
    this.postAddress = this.postAddress.bind(this);
  }

  public async getAvailablePoints(this: Customer, shopId: number): Promise<IPoints[]> {
    if (this.customerId === 0) {
      return [];
    }

    return await CustomerRequestHandler.getAvailablePoints(shopId, this.customerId);
  }

  /**
   * Logs a customer in
   * @param {ICredentials} credentials
   * @returns {Promise<boolean>} Whether the login was successful or not
   */
  public async logIn(this: Customer, credentials: ICredentials): Promise<boolean> {
    const visitorData = await CustomerRequestHandler.loginCustomer(credentials);
    if (!visitorData?.token) {
      return false;
    }
    const token = Customer.verifyToken(visitorData.token);

    // This should be adjusted, so that the `waitForToken()` method
    // would return true for all following requests that need a token
    if (window.c_CustomerIdentifier !== String(token.customer_id)) {
      // eslint-disable-next-line camelcase
      window.c_CustomerIdentifier = String(token.customer_id);
    }

    this.eventEmitter.emit({token, visitorData});

    return true;
  }

  /**
   * Logs the customer out
   * @returns {Promise<void>}
   */
  public async logOut(this: Customer): Promise<void> {
    await CustomerRequestHandler.logoutCustomer();
    const visitorData = await CustomerRequestHandler.getToken();
    this.eventEmitter.emit({
      token: null,
      visitorData,
    });
  }

  /**
   * requests customer's addresses used in the last checkout process
   * @param shopId
   */
  public async getLastAddresses(this: Customer, shopId: number): Promise<IAddressResponseData[]> {
    if (this.customerId === 0) {
      return [];
    }

    return await CustomerRequestHandler.getLastAddresses(shopId, this.customerId);
  }

  /**
   * Sets customers address by api POST call. Returns the set address or undefined if customer is unknown
   * @param shopId sales channel id
   * @param address address properties
   */
  public async postAddress(this: Customer, shopId: number, address: ICustomerAddress): Promise<void> {
    if (!this.customerId) {
      throw new Error("Tried a POST address without an existing customer id");
    }

    return await CustomerRequestHandler.postAddress(shopId, this.customerId, address);
  }

  /**
   * request magic link via mail
   * @param email recipients email address
   * @param redirectUrl url placed in the email to redirect the user to
   */
  public async requestMagicLink(email: string, redirectUrl: string): Promise<void> {
    await CustomerRequestHandler.requestMagicLink(email, redirectUrl);
  }
  /**
   * request new password via mail
   * @param email
   */
  public async requestPassword(email: string): Promise<void> {
    await CustomerRequestHandler.requestPassword(email);
  }

  /**
   * Puts newsletter options to user
   * @param {number} shopId
   * @param data
   * @returns {Promise<INewsletter[]>}
   */
  public async setNewsletterOptions(this: Customer, shopId: number, data: INewsletter[]): Promise<INewsletter[]> {
    return await CustomerRequestHandler.setNewsletterOptions(shopId, this.customerId, data);
  }

  /**
   * Gets newsletter options to user
   * @param {number} shopId
   * @returns {Promise<INewsletter[]>}
   */
  public async getNewsletterOptions(this: Customer, shopId: number): Promise<INewsletter[]> {
    return await CustomerRequestHandler.getNewsletterOptions(shopId, this.customerId);
  }

  /**
   * Gets ratings to user
   * @param {number} shopId
   * @param {number | null} limit
   * @param {number | null} offset
   * @param {boolean | null} isRated
   * @returns {Promise<ICustomerRatingsResult[]>}
   */
  public async getRatings(
    this: Customer,
    shopId: number,
    limit: number | null,
    offset: number | null,
    isRated: boolean | null,
  ): Promise<ICustomerRatingsResult[]> {
    return await CustomerRequestHandler.getRatings(shopId, this.customerId, limit, offset, isRated);
  }

  public async getReceipts(this: Customer, shopId: number, type: ReceiptType | null): Promise<IReceiptData[]> {
    return await CustomerRequestHandler.getReceipts(shopId, this.customerId, type);
  }

  /**
   * Retrieves customer's properties or returns undefined if customer is unknown
   * @param shopId sales channel id
   */
  public async retrieveProperties(this: Customer, shopId: number): Promise<ICustomerPutParams | undefined> {
    if (!this.customerId) {
      throw new Error("Tried a POST address without an existing customer id");
    }

    return await CustomerRequestHandler.retrieveProperties(shopId, this.customerId);
  }

  /**
   * Sets customer's properties by customerId and token, or returns undefined if customer is
   * unknown. If customer doesn't exist or is not recognised (no token/customerId == 0) method
   * `CustomerFactory.createOrUpdateCustomer(<customersEmailAddress>)` has to be called.
   * @param shopId Sales channel id
   * @param properties
   */
  public async putProperties(
    this: Customer,
    shopId: number,
    properties: ICustomerPutParams,
  ): Promise<ICustomerPutParams | undefined> {
    if (!this.customerId) {
      throw new Error("Tried a PUT properties without an existing customer id");
    }

    return await CustomerRequestHandler.updateProperties(shopId, this.customerId, properties);
  }

  public async newsletterOptin(this: Customer, shopId: number): Promise<void> {
    await CustomerRequestHandler.newsletterOptin(shopId, this.customerId);
  }

  public async newsletterOptout(this: Customer, shopId: number) {
    await CustomerRequestHandler.newsletterOptout(shopId, this.customerId);
  }

  /**
   * Whether the customer is recognized (at least)
   * @returns {boolean}
   */
  public get isRecognised(): boolean {
    return this.hasRole(Roles.SHOP);
  }

  /**
   * An ID identifying the visitor
   * @returns {string}
   */
  public get visitorId(): string {
    return this.visitorData.visitorId;
  }

  /**
   * The raw jw-token, if present
   * @returns {string | null}
   */
  public get token(): string | null {
    return this.visitorData.token;
  }

  /**
   * Whether the customer is logged in
   * @returns {boolean}
   */
  public get isLoggedIn(): boolean {
    return this.hasRole(Roles.SHOP_ELEVATED);
  }

  /**
   * return if customer is registered for newsletter
   * @returns {boolean}
   */
  public get newsletterFlag(): boolean {
    return !!this.parsedToken?.newsletter_flag;
  }

  /**
   * Whether the customer has a specific role
   * @param {Roles} role
   * @returns {boolean}
   */
  private hasRole(role: Roles): boolean {
    if (!this.parsedToken) {
      return false;
    }

    return this.parsedToken.realm_access.roles.indexOf(role) > -1;
  }

  /**
   * The customers email address
   * @returns {string | null}
   */
  public get email(): string | null {
    return this.parsedToken ? this.parsedToken.email : null;
  }

  /**
   * The isCorporateCustomer flag
   * @returns {boolean}
   */
  public get isCorporateCustomer(): boolean {
    return this.parsedToken ? this.parsedToken.isCorporateCustomer : false;
  }

  /**
   * The customers id
   * @returns {number}
   */
  public get customerId(): number {
    return this.parsedToken ? this.parsedToken.customer_id : 0;
  }

  /**
   * The group of customers this specific customer belongs to
   * @returns {number}
   */
  public get customerGroupId(): number {
    return this.parsedToken ? this.parsedToken.customer_group_id : 0;
  }

  /**
   * The emitter to listen on for CustomerChange events
   * @returns {IEventEmitter<ICustomerDataset>}
   */
  public get emitter(): IEventEmitter<ICustomerDataset> {
    return this.eventEmitter;
  }
}

export interface IOurJwtToken extends JwtPayload {
  iss: string;
  sub: string;
  jti: string;
  aud: string;
  exp: number;
  iat: number;
  email: string;
  realm_access: {
    roles: Roles[];
  };
  customer_id: number;
  customer_group_id: number;
  newsletter_flag: boolean;
  isCorporateCustomer: boolean;
}

export enum Roles {
  SHOP = "SHOP",
  SHOP_ELEVATED = "SHOP_ELEVATED",
}
